import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Routes } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { ObsWizardContext } from "@/features/obs/hooks/obs-wizard-context";
import type {
  ObsWizardValue,
  SlugCheck,
} from "@/features/obs/hooks/obs-wizard-context";
import {
  clearDraft,
  EMPTY_DRAFT,
  isDraftStarted,
  readDraftData,
  readDraftFile,
  tileFileKey,
  writeDraftData,
  writeDraftFile,
  type ObsDraftData,
} from "@/features/obs/lib/draft-store";
import {
  EMPTY_TILES,
  measureImage,
  type TileFiles,
  type TileLocale,
} from "@/features/obs/lib/tiles";
import {
  resolveStep,
  type StepGates,
  type WizardStep,
} from "@/features/obs/lib/wizard-steps";
import {
  useCreateObsReport,
  useDeleteObsReport,
  useObsChartOptions,
  useObsReports,
  useProfileObsWorkbook,
  useUpdateObsReport,
  useUploadObsTile,
  useUploadObsWorkbook,
} from "@/features/obs/hooks/useObsQueries";
import { handleApiError } from "@/lib/error-handler";
import type {
  ObsReport,
  ObsRole,
  ObsSheetProfile,
  ObsUploadResult,
} from "@/types/obs";

/**
 * Create a new OBS report from a workbook nobody has described before.
 *
 * 🔑 THE IDEA THAT MAKES THIS SAFE. "We do not know this file's structure" is a
 * ONE-TIME COST AT CREATION, not a permanent property of the system:
 *
 *     upload → profile → OPERATOR CONFIRMS THE MAPPING → declare the units
 *            → preview the real screen → publish, and SAVE THE MAPPING
 *
 * After that, every later upload for this report validates against its stored
 * mapping exactly the way the six validate against obs-schema.json today. So the
 * whole safety model survives for all ongoing updates; only the single creation
 * step is unguarded, and it is guarded by a person plus a preview.
 *
 * 🔴 WHAT THE PROFILER IS NOT ALLOWED TO DECIDE:
 *  · BUDGET vs ACTUAL. Purely semantic, and the ministry's own headers vary.
 *    Swap them and every execution rate inverts behind a chart that still looks
 *    right — strictly worse than a rejected file, because nothing looks broken.
 *    So both carry an explicit confirmation the operator must tick.
 *  · THE CURRENCY AND SCALE. Nothing in a file says whether 25,406 is dinars,
 *    thousands or millions. An earlier version inferred the currency from the
 *    divisor and labelled dinar datasets «مليون دولار». They are declared by hand.
 *
 * ⚠️ THE UNIT-MISMATCH GUARD CANNOT FIRE ON THE FIRST UPLOAD. That check works by
 * comparing against a previous version of the same report, and a brand-new report
 * has no baseline — the upload where a units mistake is MOST likely is the one
 * with no automatic defence. The grand totals are therefore shown at full width
 * before publishing, because reading "١٬٢٣٤ ترليون" and knowing it should be
 * billions is the only check left.
 *
 * The preview is the REAL screen, not an approximation: the report is created and
 * its data uploaded while it is still unpublished, and an unpublished report keeps
 * resolving at its own URL (it simply stays off the tile grid). So the operator
 * sees the actual charts, drawn by the actual renderer, before anything is
 * visible to the public.
 *
 * 🔑 THIS WAS A MODAL UNTIL 2026-08-09. It is now six routes under one layout,
 * with the draft persisted (see draft-store) — so the browser's own back button
 * moves between steps, a step can be reloaded, and nothing typed is lost by any
 * of it. What did NOT change is the order or the guarantees: the reachability
 * rules in wizard-steps.ts re-derive from the draft what the modal knew from
 * never letting anyone skip.
 */

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const LIST_PATH = appPath(Routes.OBS_REPORTS);
const WIZARD_PATH = appPath(Routes.OBS_REPORTS, "new");

const fileSig = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

export function ObsWizardProvider({
  urlStep,
  children,
}: {
  /**
   * The step named in the address bar, or null at the wizard's bare URL. Null is
   * a real case, not a missing value: `/admin/obs-reports/new` is where the
   * dashboard's own button points, and it means "wherever I left off".
   */
  urlStep: WizardStep | null;
  children: ReactNode;
}) {
  const navigate = useNavigate();

  const [draft, setDraft] = useState<ObsDraftData>(EMPTY_DRAFT);
  const [workbook, setWorkbook] = useState<File | null>(null);
  const [tiles, setTiles] = useState<TileFiles>(EMPTY_TILES);
  const [hydrated, setHydrated] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  /**
   * Deliberately NOT persisted. A published report is not a draft, and a stored
   * "already published" flag is exactly the sort of state that outlives its truth
   * — resuming into a receipt for something finished last week.
   */
  const [published, setPublished] = useState<ObsUploadResult | null>(null);

  /** Writes are suppressed before hydration (would clobber) and after the draft is deliberately forgotten. */
  const hydratedRef = useRef(false);
  const persistRef = useRef(true);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const profileWorkbook = useProfileObsWorkbook();
  const createReport = useCreateObsReport();
  const removeReport = useDeleteObsReport();
  const updateReport = useUpdateObsReport();
  const uploadWorkbook = useUploadObsWorkbook();
  const uploadTile = useUploadObsTile();

  /* ---------------------------------------------------------------- hydrate */

  useEffect(() => {
    let cancelled = false;
    const stored = readDraftData();
    if (stored) {
      setDraft(stored);
      setResumed(isDraftStarted(stored));
    }
    // The answers are readable synchronously; the files are not, and the wizard
    // must not decide which step is reachable until both halves are in — a draft
    // whose workbook has not arrived yet looks, for one render, like a draft that
    // never had one.
    void Promise.all([
      readDraftFile("workbook"),
      readDraftFile("tile:ar"),
      readDraftFile("tile:en"),
    ]).then(([wb, ar, en]) => {
      if (cancelled) return;
      setWorkbook(wb);
      setTiles({ ar, en });
      hydratedRef.current = true;
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------------------------------------------------------- persist */

  useEffect(() => {
    if (!hydratedRef.current) return;
    // Debounced: `profile` runs to tens of kilobytes and every keystroke in a
    // title would otherwise re-serialise all of it.
    const timer = setTimeout(() => {
      // 🔴 RE-CHECKED HERE, NOT ONLY ABOVE. A pending write scheduled while the
      // draft was still live would otherwise land AFTER `forget()` had wiped
      // storage — publishing clears the draft without touching `draft` itself,
      // so nothing re-runs this effect and nothing cancels the timer. The draft
      // would quietly rise from the dead, 250ms after the report went live.
      if (!persistRef.current) return;
      writeDraftData(draft);
    }, 250);
    return () => clearTimeout(timer);
  }, [draft]);

  // The debounce above is cancelled by unmounting, which would drop whatever was
  // typed in the last quarter-second before navigating away. Flush it.
  useEffect(
    () => () => {
      if (hydratedRef.current && persistRef.current) {
        writeDraftData(draftRef.current);
      }
    },
    [],
  );

  // Remember where they were, so the wizard's bare URL reopens on the right step.
  // Only when the URL NAMES one: at the bare URL `step` is a placeholder we are
  // about to redirect away from, and recording it would overwrite the very value
  // being read.
  useEffect(() => {
    if (!hydratedRef.current || !urlStep) return;
    setDraft((d) => (d.lastStep === urlStep ? d : { ...d, lastStep: urlStep }));
  }, [urlStep]);

  const forget = useCallback(async () => {
    persistRef.current = false;
    await clearDraft();
  }, []);

  /* ----------------------------------------------------------------- basics */

  const patch = useCallback((partial: Partial<ObsDraftData>) => {
    setDraft((d) => ({ ...d, ...partial }));
  }, []);

  const goTo = useCallback(
    (next: WizardStep) => navigate(`${WIZARD_PATH}/${next}`),
    [navigate],
  );

  const sheet: ObsSheetProfile | null = useMemo(
    () => draft.profile?.sheets.find((s) => s.name === draft.sheetName) ?? null,
    [draft.profile, draft.sheetName],
  );

  const proposalFor = useCallback(
    (role: ObsRole) => sheet?.proposals.find((p) => p.role === role) ?? null,
    [sheet],
  );

  const slugValid = SLUG_RE.test(draft.slug) && draft.slug.length <= 64;

  /**
   * 🔴 IS THE SLUG STILL FREE? ASKED ON STEP ONE, NOT LEFT TO STEP FOUR.
   *
   * The server has always refused a duplicate — `create` rejects a slug that
   * already exists, in one namespace shared with the six declared reports. But
   * that answer only arrived when «إنشاء ورفع البيانات» was pressed on the UNITS
   * step, by which point the operator had picked a workbook, confirmed every
   * column role and declared the units — and none of that survives changing the
   * slug, because the slug is the one field no PATCH can move. The only way out
   * of a collision discovered there is to walk the whole wizard again.
   *
   * `GET /obs-reports` returns every report, published or not — exactly the rows
   * the server's own check reads — so the question is answered from the list the
   * dashboard already has, with no new endpoint and no request per keystroke.
   *
   * ⚠️ THIS FAILS OPEN, AND IS AN EARLY WARNING RATHER THAN THE GUARANTEE. A
   * list that has not loaded, or that failed, reports every slug as free; and
   * queries here tolerate five minutes of staleness, so a report created moments
   * ago in another session can still be missed. The create call's own rejection
   * stays exactly where it was, behind this.
   */
  const reports = useObsReports();
  const slugConflict: ObsReport | null = useMemo(() => {
    // A slug THIS wizard created is in that list by definition, and it is not a
    // conflict — it is this report. Without this guard the units step would
    // create the report and then bounce the operator back to step one to be told
    // their own slug was taken.
    if (!slugValid || draft.createdSlug) return null;
    return reports.data?.find((r) => r.slug === draft.slug) ?? null;
  }, [draft.createdSlug, draft.slug, reports.data, slugValid]);
  /**
   * WHERE THE ANSWER STANDS — four states, because "not taken" and "we do not
   * know" are different things and only one of them may be shown as available.
   *
   * 🔴 `unknown` EXISTS BECAUSE OF A DEFECT THIS CHECK SHIPPED WITH. Deriving it
   * from `isLoading` alone left «التالي» disabled for the WHOLE retry chain —
   * queries here retry a 5xx three times with backoff, so a broken list wedged
   * step one for the better part of ten seconds and then quietly opened. A first
   * failed attempt is enough to stop waiting: `failureCount` moves on the first
   * rejection while `isPending` stays true through the retries.
   *
   * Only `checking` holds the button, and it is deliberately NOT a gate — a gate
   * that went false while a request was in flight would throw a RESUMED draft
   * back to step one for as long as the load took.
   */
  const slugCheck: SlugCheck =
    !slugValid || draft.createdSlug
      ? "idle"
      : slugConflict
        ? "taken"
        : reports.data
          ? "free"
          : reports.isError || reports.failureCount > 0
            ? "unknown"
            : "checking";

  const hasMeasure = Boolean(draft.roles.budget || draft.roles.actual);
  const gates: StepGates = {
    // A TAKEN slug fails this gate, not merely a malformed one. Every later step
    // writes to a report at that address, so none of them can say anything true
    // while the address belongs to someone else.
    identityDone: slugValid && !slugConflict && draft.titleAr.trim().length > 0,
    hasProfile: Boolean(draft.profile),
    mappingDone:
      Boolean(sheet) &&
      Boolean(draft.roles.year) &&
      hasMeasure &&
      draft.measuresConfirmed,
    hasData: Boolean(draft.uploaded?.applied),
  };
  const unitsDone =
    draft.unitAr.trim().length > 0 &&
    draft.scaleUnitAr.trim().length > 0 &&
    Number(draft.scale) > 0;

  /**
   * localStorage and IndexedDB can disagree: the answers restore synchronously
   * while the file read can fail outright (a blocked database, a browser that
   * evicted it, a private window). The draft then knows a workbook's NAME but
   * holds no bytes — and the units step's button would do nothing at all, which
   * is the worst of the three possible behaviours.
   */
  const needsWorkbookAgain =
    hydrated && !workbook && Boolean(draft.workbookName) && !gates.hasData;

  const saving =
    createReport.isPending ||
    updateReport.isPending ||
    uploadWorkbook.isPending ||
    uploadTile.isPending;
  const busy = profileWorkbook.isPending || saving;

  /**
   * 🔑 THE STEP THE OPERATOR IS ACTUALLY ON, which is not always the one they
   * asked for. The address bar is an input this wizard does not control — anyone
   * can type `/new/charts` before a workbook exists, or come back to a bookmark
   * whose draft has since been published — so the requested step is resolved
   * against what the draft can support, and the URL is then corrected to match.
   *
   * Resolving in RENDER rather than only in the effect matters: it means no step
   * ever draws with nothing behind it, not even for the single frame before the
   * redirect lands.
   */
  const step: WizardStep = resolveStep(urlStep ?? draft.lastStep, gates);

  useEffect(() => {
    // Until the files are back, `gates` is reading a half-restored draft and
    // would send a perfectly good draft back to step one.
    if (!hydrated) return;
    if (urlStep !== step) {
      navigate(`${WIZARD_PATH}/${step}`, { replace: true });
    }
  }, [hydrated, navigate, step, urlStep]);

  // Enabled only once a workbook has landed: before that the server would
  // truthfully answer "no data, no options" and the request is noise.
  const chartOptions = useObsChartOptions(
    draft.slug,
    step === "charts" && gates.hasData,
  );

  /* ------------------------------------------------------------------ files */

  const pickWorkbook = useCallback(
    (picked: File) => {
      setWorkbook(picked);
      void writeDraftFile("workbook", picked);
      setDraft((d) => ({
        ...d,
        workbookName: picked.name,
        profile: null,
        sheetName: "",
        roles: {},
        measuresConfirmed: false,
      }));
      profileWorkbook
        .mutateAsync({ file: picked })
        .then((result) => {
          const first = result.suggestedSheet ?? result.sheets[0]?.name ?? "";
          const chosen = result.sheets.find((s) => s.name === first);
          setDraft((d) => ({
            ...d,
            profile: result,
            sheetName: first,
            // Seed the form with the proposals, so the operator CORRECTS rather
            // than fills in from scratch — but every one of them is still a field
            // they can change, and the measures still need an explicit tick.
            roles: Object.fromEntries(
              (chosen?.proposals ?? []).map((p) => [p.role, p.header]),
            ) as Partial<Record<ObsRole, string>>,
          }));
          goTo("mapping");
        })
        .catch(handleApiError);
    },
    [goTo, profileWorkbook],
  );

  const pickTile = useCallback((locale: TileLocale, picked: File | null) => {
    setTiles((t) => ({ ...t, [locale]: picked }));
    void writeDraftFile(tileFileKey(locale), picked);
    setDraft((d) => ({
      ...d,
      tileNames: { ...d.tileNames, [locale]: picked?.name ?? null },
      tileSizes: { ...d.tileSizes, [locale]: null },
    }));
    if (picked) {
      measureImage(picked, (size) =>
        setDraft((d) => ({
          ...d,
          tileSizes: { ...d.tileSizes, [locale]: size },
        })),
      );
    }
  }, []);

  const pickSheet = useCallback(
    (name: string) => {
      const chosen = draft.profile?.sheets.find((s) => s.name === name);
      setDraft((d) => ({
        ...d,
        sheetName: name,
        roles: Object.fromEntries(
          (chosen?.proposals ?? []).map((p) => [p.role, p.header]),
        ) as Partial<Record<ObsRole, string>>,
        measuresConfirmed: false,
      }));
    },
    [draft.profile],
  );

  /* ---------------------------------------------------------------- actions */

  /** Create the report, then upload its first workbook — still unpublished. */
  const createAndUpload = useCallback(() => {
    if (!sheet) return;
    // The file is needed to UPLOAD, not to save the identity. A draft restored
    // without its bytes but whose figures already reached the server can still
    // come back through this step to fix a title — so the guard asks whether the
    // upload is still owed, not merely whether a file is in hand.
    if (!workbook && !draft.uploaded?.applied) return;
    const { slug, roles, createdSlug } = draft;

    const run = async () => {
      const resolvedEnRoles = Object.fromEntries(
        (["type", "category", "entity"] as const)
          .map((r) => {
            const p = proposalFor(r);
            return p?.enHeader && roles[r] === p.header ? [r, p.enHeader] : null;
          })
          .filter(Boolean) as [string, string][],
      );
      setDraft((d) => ({ ...d, enRoles: resolvedEnRoles }));

      const identity = {
        title: {
          ar: draft.titleAr.trim(),
          en: draft.titleEn.trim() || draft.titleAr.trim(),
        },
        unit: {
          ar: draft.unitAr.trim(),
          en: draft.unitEn.trim() || draft.unitAr.trim(),
        },
        scale: Number(draft.scale),
        scaleUnit: {
          ar: draft.scaleUnitAr.trim(),
          en: draft.scaleUnitEn.trim() || draft.scaleUnitAr.trim(),
        },
        valueMultiplier: Number(draft.multiplier) || 1,
        mapping: {
          sheets: [
            {
              name: sheet.name,
              roles,
              ...(Object.keys(resolvedEnRoles).length
                ? { enRoles: resolvedEnRoles }
                : {}),
            },
          ],
        },
      };

      // Create the first time through; PATCH on every later pass. Stepping back
      // and coming forward again has to be able to SAVE, or the back button is
      // decoration — and a second POST on the same slug is a duplicate-key error,
      // not an update. Everything here is patchable server-side except the slug,
      // which is why the slug field locks itself once the report exists.
      if (createdSlug) {
        await updateReport.mutateAsync({ slug, patch: identity });
      } else {
        await createReport.mutateAsync({ slug, ...identity });
        // From here the report is real: everything below edits a row that exists,
        // and leaving the wizard has to account for it rather than just forget.
        setDraft((d) => ({ ...d, createdSlug: slug }));
      }

      // The tile is optional and is attached after the report exists, since it is
      // hung off the report row. A failure here must not lose the report or its
      // data — an untiled report renders its title in the grid, which is visibly
      // incomplete rather than broken, and the tile can be added later.
      for (const locale of ["ar", "en"] as TileLocale[]) {
        const picked = tiles[locale];
        if (!picked) continue;
        const sig = fileSig(picked);
        if (draft.sentTiles[locale] === sig) continue;
        try {
          await uploadTile.mutateAsync({ slug, file: picked, locale });
          setDraft((d) => ({
            ...d,
            sentTiles: { ...d.sentTiles, [locale]: sig },
          }));
        } catch (e) {
          handleApiError(e);
        }
      }

      // No bytes to send, but the figures are already published: the identity
      // patch above was the whole job. Carry on rather than stall.
      if (!workbook) {
        goTo("charts");
        return;
      }

      // Everything the PARSE depends on. A second pass that changed only the
      // title leaves this identical, and re-uploading would append a version
      // recording no change — noise in the very history the delete dialog warns
      // about destroying.
      const sig = [
        fileSig(workbook),
        sheet.name,
        JSON.stringify(roles),
        draft.scale,
        draft.multiplier,
      ].join("|");

      if (draft.sentWorkbook === sig && draft.uploaded?.applied) {
        goTo("charts");
        return;
      }

      // force=true acknowledges WARNINGS only. There is no previous version to
      // compare against on a first upload, so there is little for it to override
      // — the server still refuses every error regardless of this flag.
      const result = await uploadWorkbook.mutateAsync({
        slug,
        file: workbook,
        force: true,
      });
      setDraft((d) => ({
        ...d,
        uploaded: result,
        ...(result.applied ? { sentWorkbook: sig } : {}),
      }));
      // Only once figures exist can the picker say what is drawable.
      goTo(result.applied ? "charts" : "mapping");
    };

    run().catch(handleApiError);
  }, [
    draft,
    goTo,
    proposalFor,
    sheet,
    tiles,
    workbook,
    createReport,
    updateReport,
    uploadTile,
    uploadWorkbook,
  ]);

  /**
   * Save the chosen charts. Skipped when none were picked — the composed screen
   * then chooses a sensible default from the data itself.
   */
  const saveCharts = useCallback(() => {
    const done = () => goTo("publish");
    if (!draft.charts.length) return done();
    updateReport
      .mutateAsync({ slug: draft.slug, patch: { charts: draft.charts } })
      .then(done)
      .catch(handleApiError);
  }, [draft.charts, draft.slug, goTo, updateReport]);

  const publish = useCallback(() => {
    updateReport
      .mutateAsync({ slug: draft.slug, patch: { isPublished: true } })
      .then(() => {
        setPublished(draft.uploaded);
        // The report is live; there is no longer a draft to come back to. The
        // receipt below stays on screen for this visit only — reloading now
        // opens a clean wizard, which is the truth.
        void forget();
      })
      .catch(handleApiError);
  }, [draft.slug, draft.uploaded, forget, updateReport]);

  /* ---------------------------------------------------------------- leaving */

  const leave = useCallback(() => navigate(LIST_PATH), [navigate]);

  /**
   * Leaving after the report exists is not the same act as leaving before it.
   *
   * Up to the units step, cancelling throws away a form. After it, the report and
   * its first dataset version are already on the server, so leaving silently left
   * a created-but-unpublished report behind — visible in the dashboard, absent
   * from the site, and never mentioned. Operators reasonably read «إلغاء» as
   * "none of this happened".
   *
   * So the question is asked instead of guessed. Abandoning it and keeping it as
   * a draft are both legitimate — finishing later is exactly why the row persists
   * — and only the operator knows which they meant.
   */
  const requestExit = useCallback(() => {
    if (draft.createdSlug && !published) {
      setDiscarding(true);
      return;
    }
    void forget().then(leave);
  }, [draft.createdSlug, forget, leave, published]);

  /** Leave the report in place, unpublished, to be finished from its card later. */
  const keepDraft = useCallback(() => {
    void forget().then(leave);
  }, [forget, leave]);

  /**
   * Remove it. Uses the ordinary delete endpoint, so the datasets go with it and
   * the media stays in `OBS-reports/<slug>/` — the same semantics as deleting
   * from a report card, not a special half-undo that only this wizard knows.
   */
  const discardDraft = useCallback(() => {
    if (!draft.createdSlug) return keepDraft();
    removeReport
      .mutateAsync({ slug: draft.createdSlug })
      .then(keepDraft)
      .catch(handleApiError);
  }, [draft.createdSlug, keepDraft, removeReport]);

  const startOver = useCallback(() => {
    // Once the report exists, "start over" is an abandonment, not a form reset —
    // so it goes through the same question, which is the only place that offers
    // deleting the row rather than orphaning it.
    if (draft.createdSlug && !published) {
      setDiscarding(true);
      return;
    }
    void clearDraft().then(() => {
      persistRef.current = true;
      setDraft(EMPTY_DRAFT);
      setWorkbook(null);
      setTiles(EMPTY_TILES);
      setPublished(null);
      setResumed(false);
      goTo("identity");
    });
  }, [draft.createdSlug, goTo, published]);

  const value: ObsWizardValue = {
    hydrated,
    resumed,
    dismissResumeNotice: useCallback(() => setResumed(false), []),
    startOver,

    step,
    goTo,
    gates,

    draft,
    patch,

    workbook,
    tiles,
    pickWorkbook,
    pickTile,
    needsWorkbookAgain,

    sheet,
    pickSheet,
    proposalFor,

    slugValid,
    slugConflict,
    slugCheck,
    unitsDone,
    busy,
    profiling: profileWorkbook.isPending,
    saving,

    chartOptions,

    published,
    createAndUpload,
    saveCharts,
    publish,

    discarding,
    closeDiscardPrompt: useCallback(() => setDiscarding(false), []),
    requestExit,
    keepDraft,
    discardDraft,
    removing: removeReport.isPending,
  };

  return (
    <ObsWizardContext.Provider value={value}>
      {children}
    </ObsWizardContext.Provider>
  );
}
